import React from 'react';
import { Plus, Trash2, Check, Sparkles } from 'lucide-react';

interface PlanFeaturesListProps {
  features: string[];
  onAddFeature: (text?: string) => void;
  onUpdateFeature: (index: number, val: string) => void;
  onRemoveFeature: (index: number) => void;
}

const COMMON_FEATURE_SUGGESTIONS = [
  'Unlimited API Requests',
  'Priority 24/7 Support',
  'Custom Domain & Branding',
  'Team Collaboration & Roles',
  'Advanced Analytics & Reports',
  'Automated Invoicing & Dunning',
];

const PlanFeaturesList: React.FC<PlanFeaturesListProps> = ({
  features,
  onAddFeature,
  onUpdateFeature,
  onRemoveFeature,
}) => {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">
          Features It Gives
        </label>
        <button
          type="button"
          onClick={() => onAddFeature('')}
          className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 flex items-center gap-1"
        >
          <Plus className="w-3.5 h-3.5" />
          Add Feature
        </button>
      </div>

      <div className="space-y-2">
        {features.map((feature, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 flex items-center justify-center shrink-0">
              <Check className="w-3.5 h-3.5" />
            </div>
            <input
              type="text"
              placeholder={`e.g. Feature benefit ${idx + 1}`}
              value={feature}
              onChange={e => onUpdateFeature(idx, e.target.value)}
              className="flex-1 px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-medium text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
            />
            {features.length > 1 && (
              <button
                type="button"
                onClick={() => onRemoveFeature(idx)}
                className="p-2 text-slate-400 hover:text-red-500 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                title="Remove feature"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Quick Suggestions */}
      <div className="pt-2">
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1">
          <Sparkles className="w-3 h-3 text-indigo-500" /> Click to add common features:
        </p>
        <div className="flex flex-wrap gap-1.5">
          {COMMON_FEATURE_SUGGESTIONS.map(sug => {
            const alreadyAdded = features.includes(sug);
            if (alreadyAdded) return null;
            return (
              <button
                key={sug}
                type="button"
                onClick={() => onAddFeature(sug)}
                className="text-[11px] px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-indigo-50 hover:text-indigo-600 text-slate-600 dark:text-slate-300 font-medium transition-colors"
              >
                + {sug}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PlanFeaturesList;
